import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

export type Tab = {
  /** Identifiant stable, sert aux attributs ARIA. */
  id: string;
  label: string;
  /**
   * Libelle court, affiche SOUS `md` uniquement. Les trois libelles complets font ~60
   * caracteres : sur un telephone de 360px ils ne tiennent sur aucune ligne, et le 3e
   * onglet sortait de l'ecran sans que rien ne signale qu'on pouvait faire defiler.
   *
   * Le libelle COMPLET reste l'`aria-label` : un lecteur d'ecran annonce toujours
   * « Connexions a distance », jamais « A distance ».
   */
  shortLabel?: string;
  /** Icone affichee sous `md`, pour identifier l'onglet quand le texte est reduit. */
  icon?: IconProp;
  to: string;
};

/**
 * Barre d'onglets (Historique, Profil).
 *
 * Les onglets sont des LIENS, pas des boutons : chaque onglet a sa propre URL, de sorte
 * qu'on puisse partager un lien vers le bon onglet et que le bouton Retour du navigateur
 * fonctionne. Un clic-droit « ouvrir dans un nouvel onglet » doit marcher — un <button>
 * le casserait.
 *
 * Motif ARIA standard (tablist / tab / aria-selected), avec navigation clavier ← →.
 *
 * L'onglet actif est signalé par la COULEUR **et** une bordure basse **et**
 * `aria-selected` : la teinte seule exclurait les daltoniens et l'impression N&B.
 */
export const Tabs = ({ tabs, activeId }: { tabs: Tab[]; activeId: string }) => {
  // Flèches ← → : déplacer le focus d'un onglet à l'autre, en bouclant aux extrémités.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;

    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLAnchorElement>('[role="tab"]')
    );
    const current = items.findIndex((el) => el === document.activeElement);
    if (current === -1) return;

    e.preventDefault();
    const next =
      e.key === "ArrowRight"
        ? (current + 1) % items.length
        : (current - 1 + items.length) % items.length;
    items[next].focus();
  };

  return (
    <div
      role="tablist"
      onKeyDown={onKeyDown}
      className="mt-4 flex gap-1 border-b border-grey-light"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <Link
            key={tab.id}
            to={tab.to}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`panel-${tab.id}`}
            // Un onglet non actif reste atteignable au clavier via les flèches, pas
            // via Tab : c'est le motif ARIA (un seul point d'entrée dans la barre).
            tabIndex={isActive ? 0 : -1}
            // `aria-label` porte TOUJOURS le libelle complet, meme quand l'affichage
            // est reduit : l'information ne doit pas dependre de la largeur d'ecran.
            aria-label={tab.label}
            title={tab.label}
            className={`flex-1 md:flex-none flex flex-col md:flex-row items-center justify-center md:justify-start gap-0.5 md:gap-2 px-1 md:px-4 py-2 text-sm font-medium border-b-2 -mb-px min-w-0 ${
              isActive
                ? "border-secondary text-default-text"
                : "border-transparent text-grey-readable hover:text-default-text"
            }`}
          >
            {tab.icon && (
              <FontAwesomeIcon
                icon={tab.icon}
                className="text-base md:text-sm"
              />
            )}
            {/* Deux libelles, un seul visible a la fois : le court sur telephone, le
                complet a partir de `md`. `truncate` + `min-w-0` evitent qu'un libelle
                un peu long ne repousse les deux autres onglets hors de l'ecran. */}
            <span className="md:hidden text-xs leading-tight truncate max-w-full">
              {tab.shortLabel ?? tab.label}
            </span>
            <span className="hidden md:inline whitespace-nowrap">
              {tab.label}
            </span>
          </Link>
        );
      })}
    </div>
  );
};
